import PropTypes from 'prop-types'
import { Provider } from 'react-redux'
import React, { useEffect, useMemo, useState } from 'react'
import { BOOTSTRAP_PHASE_COMPLETE, BOOTSTRAP_PHASE_INIT } from 'services/app'
import HistoryContext from 'components/Router/HistoryContext'
import { setAppBootstrapPhase } from 'services/app/actions'
import headerRoutes from 'routes/header-routes'
import footerRoutes from 'routes/footer-routes'
import FooterApp from 'components/FooterApp'
import HeaderApp from 'components/HeaderApp'
import mainRoutes from 'routes/routes'
import Router from 'components/Router'
import Head from 'components/Head'
import Main from 'components/Main'
import { ErrorBoundary } from 'react-error-boundary'
import { H1, HEADING_TYPES } from 'components/Heading'
import { addNewRelicAction } from 'services/app/_newrelic'
import { getAuthIsLoggedIn } from 'services/auth'
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
} from '@apollo/client'
import { getOptimizelyFeatureClient, getOptimizelyFeatureUser } from 'services/optimizely-feature'
import { getApolloClientLinks } from 'services/apollo-graphql'
import { OptimizelyProvider } from '@optimizely/react-sdk'
import { get as getConfig } from 'config'
import _get from 'lodash/get'
import {
  HydrationContext,
  HydrationManager,
} from './Hydration'

function renderFallback (options) {
  const { message, title } = options

  return (
    <div className="app__error" role="alert">
      <div className="app__logo">
        <svg width="81" height="29" xmlns="http://www.w3.org/2000/svg"><path d="M18.508 8.369c0 1.808 1.373 3.122 3.265 3.122 1.893 0 3.232-1.213 3.232-3.017a3.416 3.416 0 00-.726-2.099 9.598 9.598 0 00-4.075-3.064 13.304 13.304 0 00-6.09-.839c-.382.03-.764.067-1.141.117C5.418 3.53 0 8.32 0 16.246c0 8.67 7.554 12.352 15.193 12.352 3.328 0 7.923-.878 9.59-1.839V15.633H14.362v.84l3.504.613v9.774c-.482.042-.965.088-1.402.088-5.913 0-8.146-5.56-8.146-11.516 0-4.726 1.86-8.6 4.533-10.379a6.333 6.333 0 012.883-1.04 8.495 8.495 0 013.912.532c.286.109.566.234.839.374.289.152.57.32.84.503a2.938 2.938 0 00-2.817 2.947zm35.699-1.343c2.237 0 3.861-1.47 3.861-3.496 0-2.028-1.679-3.53-3.861-3.53-2.182 0-3.752 1.486-3.752 3.53 0 2.043 1.544 3.496 3.752 3.496zm-9.14 18.961V14.874c0-4.197-2.846-6.585-7.811-6.585-.995 0-6.077.155-8.465 3.231a3.206 3.206 0 00-.672 1.986c0 1.678 1.221 2.837 3.035 2.837 1.813 0 3.067-1.234 3.067-2.938a2.745 2.745 0 00-2.652-2.787 8.394 8.394 0 013.244-.608h.298c2.149.063 3.383 1.527 3.383 4.012l.03 3.714h-1.68c-6.119 0-9.92 2.25-9.92 5.876 0 3.441 2.735 4.981 5.455 4.981 3.206 0 5.809-2.669 6.24-3.139l.983 2.804h7.932v-1.075l-2.468-1.196zm-8.134-1.272c-1.47 0-3.043-.654-3.043-2.492 0-2.573 2.195-2.938 3.508-2.938l1.1-.03v5.242a5.275 5.275 0 01-1.565.218zm20.35-16.422h-2.812l-6.878 2.015v1.024h3.168v15.486l-2.199.55v.898h11.009v-.944l-2.283-.592-.005-18.437zM80.727 27.18l-2.43-1.196v-11.11c0-4.196-2.845-6.584-7.81-6.584-.995 0-6.077.155-8.465 3.231a3.21 3.21 0 00-.684 1.986c0 1.678 1.22 2.837 3.034 2.837 1.813 0 3.068-1.234 3.068-2.938a2.745 2.745 0 00-2.653-2.787 8.394 8.394 0 013.245-.608h.298c2.148.063 3.382 1.527 3.382 4.012l.03 3.714h-1.68c-6.118 0-9.92 2.25-9.92 5.876 0 3.441 2.736 4.981 5.455 4.981 3.207 0 5.809-2.669 6.241-3.139l.982 2.804h7.932l-.025-1.079zM70.13 24.715c-1.469 0-3.043-.654-3.043-2.492 0-2.573 2.195-2.938 3.505-2.938l1.1-.03v5.242a5.271 5.271 0 01-1.562.218z" fill="#00C9C9" /></svg>
      </div>
      <H1 as={HEADING_TYPES.H2} className="app__error-title">
        {title}
      </H1>
      <p>{message}</p>
    </div>
  )
}

function logError (error, info, store) {
  const { message } = error
  const { componentStack } = info
  const state = store.getState()
  const { auth, user, resolver } = state
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const pageUrl = resolver.get('path') || global.location.pathname
  const dataNewRelic = {
    errorMessage: message,
    componentStack,
    isLoggedIn,
    anonymousUuid: user.getIn(['data', 'anonymousUuid']),
    country: auth.get('country'),
    username: auth.get('username'),
    pageUrl,
  }

  addNewRelicAction('AppErrorBoundary', dataNewRelic)
}

// Optimizely Feature Testing: adding OptimizelyProvider
const OptimizelyProviderWrapper = ({ state, children }) => {
  const { optimizelyFeature } = state
  const config = getConfig()
  const optimizelyFeatureTesting = !!_get(config, ['features', 'optimizelyFeatureTesting'])

  const user = optimizelyFeature.get('user') || getOptimizelyFeatureUser(state)
  const optimizely = getOptimizelyFeatureClient()

  const useOptimizelyProvider = optimizelyFeatureTesting && user && optimizely

  return useOptimizelyProvider ? (
    <OptimizelyProvider optimizely={optimizely} user={user}>
      {children}
    </OptimizelyProvider>
  ) : (
    children
  )
}

function App (props) {
  const {
    history,
    store,
  } = props
  const state = store.getState()
  const { staticText } = state
  const appStaticText = staticText.getIn(['data', 'app', 'data'])

  // keep a local state for hydrated boolean.
  // there is no getting around this since App
  // is rendered before the redux Provider, which means
  // App will not re-render when redux state is modified.
  const [hydrated, setHydrated] = useState(false)

  // create a cached hydrator object, which we will use
  // as our context to the Hydration.Provider
  const hydrator = useMemo(() => (
    HydrationManager.getContextAPI({
      onHydrationComplete: () => setHydrated(true),
    })
  ), [hydrated])

  const client = new ApolloClient({
    link: getApolloClientLinks(),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            playlistItemList: {
              keyArgs: ['playlists', 'userCurrent'],
              merge (existing = {}, incoming) {
                return incoming.offset === 0
                  ? incoming
                  : {
                    ...incoming,
                    playlistItems: [
                      ...(existing.playlistItems || []),
                      ...(incoming.playlistItems || []),
                    ],
                  }
              },
            },
          },
        },
      },
    }),
    // connectToDevTools: true,
  })

  // when App mounts, kick off the INIT phase.
  // if the hydrator is empty by the time App has mounted,
  // it means there are no async components (Suspense/Lazy) on the page
  // and we are safe to go ahead and mark hydration as complete, which will
  // then trigger the second useEffect below, settings the phase to COMPLETE.
  useEffect(() => {
    store.dispatch(setAppBootstrapPhase(
      BOOTSTRAP_PHASE_INIT,
    ))
    if (hydrator.empty()) {
      hydrator.setComplete(true)
    }
  }, [])

  // when App re-renders and hydrate is now 'true',
  // it means React's reconsiliation of async, Suspense/Lazy components
  // has finished and we are safe to tell the App bootstrapping is 'complete'
  // which will load the client-side specific UI and interactions
  // that are not supported on the server.
  useEffect(() => {
    if (hydrated) {
      store.dispatch(setAppBootstrapPhase(
        BOOTSTRAP_PHASE_COMPLETE,
      ))
    }
  }, [hydrated])

  return (
    <ErrorBoundary
      fallback={renderFallback({
        message: appStaticText.get('errorMessage'),
        title: appStaticText.get('errorTitle'),
      })}
      onError={(error, info) => logError(error, info, store)}
    >
      <ApolloProvider client={client}>
        <Provider store={store}>
          <HydrationContext.Provider value={hydrator}>
            <HistoryContext.Provider value={history}>
              <OptimizelyProviderWrapper state={state}>
                <Head />
                <HeaderApp>
                  <Router routes={headerRoutes} />
                </HeaderApp>
                <Main history={history} client={client}>
                  <Router routes={mainRoutes} />
                </Main>
                <FooterApp>
                  <Router routes={footerRoutes} />
                </FooterApp>
              </OptimizelyProviderWrapper>
            </HistoryContext.Provider>
          </HydrationContext.Provider>
        </Provider>
      </ApolloProvider>
    </ErrorBoundary>
  )
}

export default App

App.propTypes = {
  history: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
}
